// ═══════════════════════════════════════════════════════════════════════════
// drpMonthEstimate.js — a daily, FREE-data estimate of an UNANNOUNCED month's
// Class III and Class IV price, built from USDA's own FMMO formulas.
//
// One rule, three callers: api/_drp-month-estimate.js (the daily writer),
// tools/check-drp-month-estimate.js, and the DRP screen (which only READS the
// stored rows — it never recomputes, so the page and the record cannot differ).
//
// WHY (Kevin, 2026-10-20): "if you were able to determine a current predicted
// indemnity, thats what we need to show. It should change every day based on
// the market." RMA's ADM stops publishing a quarter's expected prices when its
// sales close (Q4-2026: last 2026-09-15), SILAS stores no milk futures and paid
// data is off the table — so an in-progress quarter had NO price for its
// unannounced months and the screen printed "No trigger" over a real loss.
//
// ── SOURCES ────────────────────────────────────────────────────────────────
// Formula: 7 CFR 1000.50(h)-(o) as amended by 90 FR 6603 (2025-01-17):
//   effective 2025-06-01 — make allowances cheese 0.2519 / butter 0.2272 /
//   NFDM 0.2393 / dry whey 0.2668, butterfat recovery 0.91, cheese yield 1.589,
//   40-lb BLOCK only (barrels dropped from the survey);
//   effective 2025-12-01 (amendatory instruction 3a) — skim composition
//   3.3% protein / 6.0% other solids / 9.3% nonfat solids (was 3.1 / 5.9 / 9.0).
//   Months before 2025-06 used barrel cheese, which ndpsr_weekly does not store
//   (see api/ams-sync.js), so they are NOT modelled: regimeFor() returns null.
// Product prices: NDPSR (LMPR 2993, ndpsr_weekly) for weeks USDA has published;
//   CME Group daily cash closes (USDA Dairy Market News, MARS report 1603, free
//   with the MARS key this repo already holds) for weeks it has not.
//
// ── WHICH WEEKS MAKE A MONTH (MEASURED) ─────────────────────────────────────
// USDA: the final class prices weight "the most recent four or five weeks
// (since the last monthly Announcement) from the latest NDPSR". Operationalised
// as: an NDPSR week (week_ending = Saturday) belongs to the month of its FRIDAY
// (week_ending − 1 day), at the LATEST value USDA had published by then. Replayed
// over every announced month 2025-06 … 2026-09 this reproduces all 32 announced
// Class III and IV prices to the cent (max error $0.00). Assigning by the
// Saturday instead, or by the report's publication month, misses by up to 44¢
// / 89¢ — so the assignment is load-bearing, and it is a measurement, not a cite.
//
// ── CME → NDPSR (MEASURED, 2025-01 … 2026-10, 91 weeks per product) ─────────
// NDPSR prices SALES, which lag the CME spot market. The implied NDPSR price for
// week ending S is the mean CME close over the 14 days ending S − 14 (CME_LAG_DAYS,
// CME_SPAN_DAYS), plus that product's own trailing bias (NDPSR − implied) over
// the last BIAS_WEEKS published weeks, point in time. Measured MAE at that
// mapping: butter 5.0¢, cheese 4.4¢, NFDM 4.5¢, whey 3.1¢/lb; raw bias
// butter +1.3¢, cheese +4.3¢, NFDM −1.9¢, whey −2.0¢.
//
// ── WHAT IT REFUSES ─────────────────────────────────────────────────────────
// * A week whose CME window is not yet complete takes the latest 14-day CME
//   average and is COUNTED as carried forward; a month with any carried week is
//   labelled carried-forward (a month not started is entirely carried).
// * No CME at all → the latest published NDPSR price is carried (ndpsr-only).
// * No regime for the month → null. Never a guessed formula.
// An announced price ALWAYS beats this (the DRP screen checks announced first).
// ═══════════════════════════════════════════════════════════════════════════

const MODEL_VERSION = "drp-month-est-v1";
const PRODUCTS = ["butter", "cheese_block", "nfdm", "dry_whey"];
const CME_LAG_DAYS = 14;
const CME_SPAN_DAYS = 14;
const BIAS_WEEKS = 26;
const MIN_BIAS_WEEKS = 8;

const FMMO_2025_06 = Object.freeze({
  id: "fmmo-2025-06",
  source: "7 CFR 1000.50 as amended 90 FR 6603 (2025-01-17), effective 2025-06-01",
  ma: Object.freeze({ cheese_block: 0.2519, butter: 0.2272, nfdm: 0.2393, dry_whey: 0.2668 }),
  bfYield: 1.211, nfsYield: 0.99, osYield: 1.03,
  proteinYield: 1.383, cheeseBfYield: 1.589, bfRecovery: 0.91, proteinAdj: 1.17,
  skim: Object.freeze({ protein: 3.1, otherSolids: 5.9, nonfatSolids: 9.0 }),
});
const FMMO_REGIMES = Object.freeze([
  Object.freeze({ ...FMMO_2025_06, from: "2025-06", to: "2025-11" }),
  Object.freeze({ ...FMMO_2025_06, id: "fmmo-2025-12", from: "2025-12", to: null,
    source: "7 CFR 1000.50(i),(k) as amended 90 FR 6603 instruction 3a, effective 2025-12-01",
    skim: Object.freeze({ protein: 3.3, otherSolids: 6.0, nonfatSolids: 9.3 }) }),
]);

function regimeFor(monthKey) {
  if (!/^\d{4}-\d{2}$/.test(String(monthKey || ""))) return null;
  return FMMO_REGIMES.find((r) => monthKey >= r.from && (r.to == null || monthKey <= r.to)) || null;
}

// USDA rounds component prices to the hundredth of a cent and skim/class to the cent.
const rnd = (x, d) => Math.round((x + (x >= 0 ? 1e-9 : -1e-9)) * 10 ** d) / 10 ** d;

// 7 CFR 1000.50(h)-(o). `p` = monthly average product prices ($/lb).
function classPricesFromProducts(p, regime) {
  if (!regime || !p) return null;
  for (const k of PRODUCTS) if (!Number.isFinite(p[k])) return null;
  const R = regime;
  const butterfat = rnd((p.butter - R.ma.butter) * R.bfYield, 4);
  const nonfatSolids = rnd((p.nfdm - R.ma.nfdm) * R.nfsYield, 4);
  const otherSolids = rnd((p.dry_whey - R.ma.dry_whey) * R.osYield, 4);
  const c = p.cheese_block - R.ma.cheese_block;
  const protein = rnd(c * R.proteinYield + (c * R.cheeseBfYield - R.bfRecovery * butterfat) * R.proteinAdj, 4);
  const skimIII = rnd(protein * R.skim.protein + otherSolids * R.skim.otherSolids, 2);
  const skimIV = rnd(nonfatSolids * R.skim.nonfatSolids, 2);
  return {
    III: rnd(0.965 * skimIII + 3.5 * butterfat, 2),
    IV: rnd(0.965 * skimIV + 3.5 * butterfat, 2),
    components: { butterfat, nonfatSolids, otherSolids, protein, skimIII, skimIV },
  };
}

const isoAdd = (iso, n) => { const t = new Date(iso + "T00:00:00Z"); t.setUTCDate(t.getUTCDate() + n); return t.toISOString().slice(0, 10); };

// The Saturdays whose week belongs to `monthKey` (Friday = week_ending − 1 in the month).
function weeksOfMonth(monthKey) {
  const [y, m] = monthKey.split("-").map(Number);
  const out = [];
  for (let d = 1; d <= 31; d++) {
    const fri = new Date(Date.UTC(y, m - 1, d));
    if (fri.getUTCMonth() !== m - 1) break;
    if (fri.getUTCDay() === 5) out.push(isoAdd(fri.toISOString().slice(0, 10), 1));
  }
  return out;
}
function monthOfWeek(weekEnding) { return isoAdd(weekEnding, -1).slice(0, 7); }

// ndpsr rows -> { product: { week_ending: [{price, sales, published}] sorted by report_date } }
function indexNdpsr(rows) {
  const idx = {};
  for (const r of rows || []) {
    if (!PRODUCTS.includes(r.product)) continue;
    const price = Number(r.price); if (!Number.isFinite(price)) continue;
    const p = (idx[r.product] = idx[r.product] || {});
    (p[r.week_ending] = p[r.week_ending] || []).push({ price, sales: Number(r.sales_lbs) || 0,
      report: String(r.report_date), published: String(r.published_date || r.report_date) });
  }
  for (const p of Object.values(idx)) for (const arr of Object.values(p)) arr.sort((a, b) => (a.report < b.report ? -1 : 1));
  return idx;
}
// The latest value USDA had published for that week by `asOf` (null if none yet).
function ndpsrAsOf(idx, product, week, asOf) {
  const arr = idx[product]?.[week]; if (!arr) return null;
  let hit = null; for (const r of arr) if (r.published <= asOf) hit = r;
  return hit;
}

// Mean CME close over the window ending `end` (inclusive), CME days on or before asOf only.
function cmeWindow(cmeSeries, end, asOf) {
  if (!cmeSeries) return null;
  const start = isoAdd(end, -(CME_SPAN_DAYS - 1));
  const v = []; for (const [d, px] of Object.entries(cmeSeries)) if (d >= start && d <= end && d <= asOf && Number.isFinite(Number(px))) v.push(Number(px));
  return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
}
function lastCmeDay(cmeSeries, asOf) {
  let last = null; for (const d of Object.keys(cmeSeries || {})) if (d <= asOf && (!last || d > last)) last = d;
  return last;
}
function cmeImpliedRaw(cmeSeries, week, asOf) {
  const end = isoAdd(week, -CME_LAG_DAYS);
  const last = lastCmeDay(cmeSeries, asOf);
  if (!last) return null;
  if (end <= last) { const v = cmeWindow(cmeSeries, end, asOf); return v == null ? null : { value: v, carried: false, windowEnd: end }; }
  const v = cmeWindow(cmeSeries, last, asOf); // window not complete yet: today's level, carried
  return v == null ? null : { value: v, carried: true, windowEnd: last };
}
// Trailing bias (NDPSR − implied) over the last BIAS_WEEKS weeks published by asOf.
function biasFor(idx, product, cmeSeries, asOf) {
  const weeks = Object.keys(idx[product] || {}).filter((w) => ndpsrAsOf(idx, product, w, asOf)).sort().slice(-BIAS_WEEKS);
  const d = [];
  for (const w of weeks) {
    const end = isoAdd(w, -CME_LAG_DAYS); const v = cmeWindow(cmeSeries, end, asOf);
    if (v != null && end <= (lastCmeDay(cmeSeries, asOf) || "")) d.push(ndpsrAsOf(idx, product, w, asOf).price - v);
  }
  return d.length >= MIN_BIAS_WEEKS ? { bias: d.reduce((a, b) => a + b, 0) / d.length, n: d.length } : { bias: 0, n: d.length, thin: true };
}

// The estimate for one month, as of `asOf` (YYYY-MM-DD). `cme` = { product: { date: close } }.
function estimateMonth({ monthKey, asOf, ndpsrRows, ndpsrIndex, cme }) {
  const regime = regimeFor(monthKey);
  if (!regime) return { monthKey, asOf, III: null, IV: null, refused: "no FMMO regime modelled for this month" };
  const idx = ndpsrIndex || indexNdpsr(ndpsrRows);
  const weeks = weeksOfMonth(monthKey);
  const monthly = {}; const inputs = {};
  let carriedWeeks = 0, cmeWeeks = 0, ndpsrWeeks = 0, ndpsrOnly = false;
  for (const product of PRODUCTS) {
    const series = cme?.[product] || null;
    const hasCme = !!lastCmeDay(series, asOf);
    const b = hasCme ? biasFor(idx, product, series, asOf) : { bias: 0, n: 0 };
    // weight for a week USDA has not published: mean sales of the last 4 published weeks
    const pub = Object.keys(idx[product] || {}).filter((w) => ndpsrAsOf(idx, product, w, asOf)).sort();
    const recent = pub.slice(-4).map((w) => ndpsrAsOf(idx, product, w, asOf).sales).filter((x) => x > 0);
    const wDefault = recent.length ? recent.reduce((a, c) => a + c, 0) / recent.length : 1;
    const lastPub = pub.length ? ndpsrAsOf(idx, product, pub[pub.length - 1], asOf) : null;
    const rows = [];
    for (const week of weeks) {
      const n = ndpsrAsOf(idx, product, week, asOf);
      if (n) { rows.push({ week, source: "ndpsr", price: n.price, weight: n.sales || wDefault }); continue; }
      const imp = hasCme ? cmeImpliedRaw(series, week, asOf) : null;
      if (imp) rows.push({ week, source: imp.carried ? "cme-carried" : "cme", price: rnd(imp.value + b.bias, 4), cmeWindowEnd: imp.windowEnd, weight: wDefault });
      else if (lastPub) rows.push({ week, source: "ndpsr-carried", price: lastPub.price, weight: wDefault });
      else return { monthKey, asOf, III: null, IV: null, refused: `no price for ${product}` };
    }
    for (const r of rows) {
      if (r.source === "ndpsr") ndpsrWeeks++;
      else if (r.source === "cme") cmeWeeks++;
      else carriedWeeks++;
      if (r.source === "ndpsr-carried") ndpsrOnly = true;
    }
    const wsum = rows.reduce((a, r) => a + r.weight, 0);
    monthly[product] = rnd(rows.reduce((a, r) => a + r.price * r.weight, 0) / wsum, 4);
    inputs[product] = { monthly: monthly[product], bias: rnd(b.bias, 4), biasWeeks: b.n, weeks: rows };
  }
  const cls = classPricesFromProducts(monthly, regime);
  const total = weeks.length * PRODUCTS.length;
  return {
    monthKey, asOf, modelVersion: MODEL_VERSION, regime: regime.id,
    III: cls.III, IV: cls.IV, components: cls.components,
    carriedForward: carriedWeeks > 0,
    basis: { weeks: weeks.length, ndpsrWeeks: ndpsrWeeks / PRODUCTS.length, cmeWeeks: cmeWeeks / PRODUCTS.length,
      carriedWeeks: carriedWeeks / PRODUCTS.length, ndpsrOnly, allCarried: carriedWeeks === total },
    inputs,
  };
}

// Months of the quarters still open as of asOf: the month containing asOf back to
// the earliest unannounced month (passed in), forward to the end of the next quarter.
function monthsToEstimate(asOf, announcedKeys) {
  const ann = new Set(announcedKeys || []);
  const [y, m] = asOf.split("-").map(Number);
  const out = [];
  // back 3 months (a month just ended is still unannounced until ~the 5th), forward to quarter end + 1 quarter
  for (let k = -3; k <= 8; k++) {
    const d = new Date(Date.UTC(y, m - 1 + k, 1)); const mk = d.toISOString().slice(0, 7);
    if (ann.has(mk) || !regimeFor(mk)) continue;
    const qEnd = Math.ceil(m / 3) * 3; // end month of current quarter
    const limit = new Date(Date.UTC(y, qEnd - 1 + 3, 1)).toISOString().slice(0, 7);
    if (mk > limit) continue;
    out.push(mk);
  }
  return out;
}

// ═══════════════════════════════════════════════════════════════════════════
// v2 — A MONTH NOT YET STARTED IS PRICED OFF RMA'S OWN FUTURES PRICES (2026-10-20)
//
// Kevin: "as accurate as we can possibly make it; matching how RMA comes up with
// announced prices for DRP, for no cost and no scraping." RMA's DRP expected price
// IS the CME futures settle (25-DRP-CEE §2(b)). Two free RMA ADM publications carry
// it per MONTH, both read straight from pubfs-rma.fpac.usda.gov:
//   * A00833 ADMDrpDailyPrice — Month1..3 Class III AND IV of every quarter still
//     ON SALE, daily (drp_adm_daily_price);
//   * ADMLivestockLgm A00600, record category 02, commodity 0847, market symbol DA —
//     LGM-Dairy's expected CLASS III price for Month2..Month11, Month k = the sales
//     month + k, one sale a WEEK. MEASURED: on all 98 LGM sales dates 2024-07-18 …
//     2026-10-15 every month it shares with that day's A00833 is IDENTICAL to the
//     cent (911 of 911). LGM carries no Class IV.
// So a month whose DRP quarter has closed keeps an official Class III price until it
// becomes LGM's Month1 (from the start of month M−1), and its Class IV only until
// its quarter's DRP sales closed. Between that print and today the board moves.
//
// THE RULE: last official price for the month, moved by what RMA's own NEAREST LATER
// month moved since (the calendar spread is held, the level is today's) — then, for
// the weeks of the month whose CME cash window is already complete, the cash nowcast
// takes over in proportion (fk). The month in progress is untouched (v1 nowcast).
// This is an INFERENCE (spread held constant), labelled as one on screen.
//
// BACKTEST (tools/drp-futures-backtest.js; 2025-07 … 2026-09, 5 quarters, n=45 per
// class), MAE of the QUARTER-average price vs RMA's actual, $/cwt:
//   Class III  v1 carry-forward 0.385  →  v2 0.255
//   Class IV   v1 carry-forward 0.739  →  v2 0.550
// Per month it LOSES once: Class III 15 days before the month starts, 0.646 vs 0.614.
// ═══════════════════════════════════════════════════════════════════════════
const FUTURES_MODEL_VERSION = "drp-month-est-v2";

const addMonths = (mk, n) => { const [y, m] = mk.split("-").map(Number); const d = new Date(Date.UTC(y, m - 1 + n, 1)); return d.toISOString().slice(0, 7); };

// LGM A00600 DA row -> { "YYYY-MM": price }: Month k (k = 2..11) = sales month + k.
// `monthPrices` is the 10 values Month2..Month11 in file order.
function lgmClassIIIMonths(salesEffectiveDate, monthPrices) {
  const m = String(salesEffectiveDate || "").match(/^(\d{4})-?(\d{2})-?\d{2}$/);
  if (!m || !Array.isArray(monthPrices)) return {};
  const sm = `${m[1]}-${m[2]}`; const out = {};
  monthPrices.forEach((p, i) => { const v = Number(p); if (p !== "" && p != null && Number.isFinite(v) && v > 0) out[addMonths(sm, i + 2)] = v; });
  return out;
}

// observations: [{ date: "YYYY-MM-DD", source: "drp"|"lgm", III: {mk: p}, IV: {mk: p} }]
function mergedOn(observations, date, cls) {
  const o = {};
  for (const ob of observations || []) if (ob.date === date) for (const [k, v] of Object.entries(ob[cls] || {})) if (Number.isFinite(Number(v))) o[k] = Number(v);
  return o;
}
function futuresImplied({ monthKey, cls, asOf, observations }) {
  const obs = (observations || []).filter((o) => o.date && o.date <= asOf);
  let last = null;
  for (const o of obs) { const v = Number(o[cls]?.[monthKey]); if (!Number.isFinite(v) || v <= 0) continue;
    if (!last || o.date > last.date || (o.date === last.date && o.source === "drp")) last = { date: o.date, source: o.source, price: v }; }
  if (!last) return null;
  let d1 = null; for (const o of obs) if (Object.keys(o[cls] || {}).length && (!d1 || o.date > d1)) d1 = o.date;
  const now = mergedOn(obs, d1, cls), then = mergedOn(obs, last.date, cls);
  const ref = Object.keys(now).filter((k) => k >= monthKey && then[k] != null).sort()[0] || null;
  const move = ref ? now[ref] - then[ref] : 0;
  return { price: rnd(last.price + move, 2), last, ref: ref ? { month: ref, date: d1, now: now[ref], then: then[ref], move: rnd(move, 2) } : null,
    stale: !ref };
}

// v1 estimate (estimateMonth) + the futures rule for a month that has not started.
function forwardEstimate({ monthKey, asOf, cash, observations }) {
  if (!cash || cash.III == null || cash.IV == null) return cash;
  if (asOf >= monthKey + "-01") return { ...cash, futures: null };          // started: v1 nowcast, unchanged
  const b = cash.basis || {}; const fk = b.weeks ? Math.max(0, Math.min(1, 1 - (b.carriedWeeks || 0) / b.weeks)) : 0;
  const out = { ...cash, futures: { fk: rnd(fk, 4), cashIII: cash.III, cashIV: cash.IV } };
  let used = 0;
  for (const cls of ["III", "IV"]) {
    const f = futuresImplied({ monthKey, cls, asOf, observations });
    out.futures[cls] = f;
    if (!f) continue;                                                         // no official print: stays carried
    out[cls] = rnd(fk * cash[cls] + (1 - fk) * f.price, 2); used++;
  }
  out.futuresUsed = used === 2 ? "both" : used === 1 ? "partial" : "none";
  return out;
}

// Stored rows (drp_month_estimates) -> what the DRP screen merges into its month
// prices: { III: {key: price}, IV: {key: price}, meta: {key: {asOf, carriedForward,
// changeIII, changeIV}} } — the latest estimate per (month, class), and the change
// from the previous estimate_date.
function latestEstimatesFromRows(rows) {
  const by = {};
  for (const r of rows || []) {
    const k = r.month_key, c = r.milk_class; if (!k || (c !== "III" && c !== "IV")) continue;
    (by[k + "|" + c] = by[k + "|" + c] || []).push(r);
  }
  const out = { III: {}, IV: {}, prev: { III: {}, IV: {} }, meta: {}, asOf: null, prevAsOf: null };
  for (const [kc, arr0] of Object.entries(by)) {
    // Newest date first; on one date the NEWEST model wins and the older model's
    // row is dropped, so a deploy day carrying v1 and v2 rows has one answer.
    arr0.sort((a, b) => (a.estimate_date !== b.estimate_date ? (a.estimate_date < b.estimate_date ? 1 : -1)
      : String(a.model_version || "") < String(b.model_version || "") ? 1 : -1));
    const arr = arr0.filter((r, i) => i === 0 || r.estimate_date !== arr0[i - 1].estimate_date);
    const [k, c] = kc.split("|"); const cur = arr[0];
    // The day's change is a MARKET move only when both days ran the same model;
    // across a model change it would print the method change as a price change.
    const prev = arr[1] && (arr[1].model_version || null) === (cur.model_version || null) ? arr[1] : null;
    const price = Number(cur.price); if (!Number.isFinite(price)) continue;
    out[c][k] = price;
    const meta = (out.meta[k] = out.meta[k] || {});
    meta.asOf = !meta.asOf || cur.estimate_date > meta.asOf ? cur.estimate_date : meta.asOf;
    meta.carriedForward = !!(meta.carriedForward || cur.carried_forward);
    if (/rma-futures/.test(String(cur.basis || ""))) meta.futures = true;
    const pp = prev ? Number(prev.price) : NaN;
    meta["change" + c] = Number.isFinite(pp) ? Math.round((price - pp) * 100) / 100 : null;
    if (Number.isFinite(pp)) { out.prev[c][k] = pp; if (!out.prevAsOf || prev.estimate_date > out.prevAsOf) out.prevAsOf = prev.estimate_date; }
    if (!out.asOf || cur.estimate_date > out.asOf) out.asOf = cur.estimate_date;
  }
  return out;
}

// The DRP screen's forward-price object: RMA's own futures-implied month price
// (drp-quote?forward=1) where RMA still publishes one, and this estimate for every
// month RMA does not — a quarter whose sales have closed. `useprev` swaps in the
// previous estimate_date's prices so the screen can show the day's change.
// Announced prices never pass through here: drpGetMonthDetail checks them first.
function mergeForwardPrices(rmaForward, est, usePrev = false) {
  const out = { III: { ...(rmaForward?.III || {}) }, IV: { ...(rmaForward?.IV || {}) },
    updated: rmaForward?.updated || null, source: rmaForward?.source || null,
    silas: { III: {}, IV: {} }, estMeta: est?.meta || {}, estAsOf: usePrev ? est?.prevAsOf || null : est?.asOf || null };
  if (!est) return out;
  const src = usePrev ? est.prev || { III: {}, IV: {} } : est;
  for (const c of ["III", "IV"]) for (const [k, v] of Object.entries(src[c] || {})) {
    if (out[c][k] != null) continue;                // RMA's published month wins
    if (!Number.isFinite(Number(v))) continue;
    out[c][k] = Number(v); out.silas[c][k] = true;
  }
  return out;
}

// The one sentence the screen and the printed summary print under an estimated
// indemnity: the date the estimate is as of, the change since the previous
// estimate (only when both days priced the same months), and which months are
// carried forward at today's market rather than built from that month's own data.
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const shortDate = (iso) => { const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})$/); return m ? `${MON[+m[2] - 1]} ${+m[3]}` : null; };
function describeEstimate({ asOf, prevAsOf, change, carriedMonths, futuresMonths }) {
  const d = shortDate(asOf); if (!d) return null;
  const parts = ["est. as of " + d];
  const p = shortDate(prevAsOf);
  if (p && Number.isFinite(change)) {
    const r = Math.round(change);
    parts.push(r === 0 ? `no change since ${p}` : `${r > 0 ? "+" : "−"}$${Math.abs(r).toLocaleString("en-US")} since ${p}`);
  }
  const names = (ks) => (ks || []).map((k) => MON[Number(String(k).slice(5, 7)) - 1]).filter(Boolean);
  const fm = names(futuresMonths);
  if (fm.length) parts.push(`${fm.join(", ")} from RMA's futures prices`);
  const fset = new Set(futuresMonths || []);
  const cm = names((carriedMonths || []).filter((k) => !fset.has(k)));
  if (cm.length) parts.push(`${cm.join(", ")} carried forward at today's market`);
  return parts.join(" · ");
}

module.exports = {
  describeEstimate,
  FUTURES_MODEL_VERSION, lgmClassIIIMonths, futuresImplied, forwardEstimate,
  mergeForwardPrices,
  MODEL_VERSION, PRODUCTS, FMMO_REGIMES, CME_LAG_DAYS, CME_SPAN_DAYS, BIAS_WEEKS, MIN_BIAS_WEEKS,
  regimeFor, classPricesFromProducts, weeksOfMonth, monthOfWeek, indexNdpsr, ndpsrAsOf,
  cmeImpliedRaw, biasFor, estimateMonth, monthsToEstimate, latestEstimatesFromRows,
};
